<script setup lang="ts">
import Icons from './Icons.vue';

// ボタンのサイズ型定義
type ButtonSize = 'sm' | 'md' | 'lg';

// ボタンのタイプ型定義
type ButtonType = 'contact' | 'info' | 'top-page' | 'compare' | 'faq';

// Propsの型定義
const props = defineProps<{
    /** ボタンのサイズ */
    size: ButtonSize;
    /** サブテキスト（エラー画面等で使用） */
    subText?: string;
    /** ボタンのタイプ（処理内容を決定） */
    buttonType?: ButtonType;  // type → buttonType に変更
}>();

// Emitsの定義
const emit = defineEmits<{
    click: [event: MouseEvent, type?: ButtonType];
}>();

// 環境に応じたcompareページのURLを取得する関数
const getCompareUrl = (): string => {
    const hostname = window.location.hostname;

    if (hostname.includes('-dev')) {
        return 'https://fire-dev.bang.co.jp/compare/';
    } else if (hostname.includes('-stg')) {
        return 'https://fire-stg.bang.co.jp/compare/';
    } else {
        // 本番環境（デフォルト）
        return 'https://fire.bang.co.jp/compare/';
    }
};

// クリックハンドラー
const handleClick = (event: MouseEvent) => {
    // ボタンタイプに応じた処理を実行
    switch (props.buttonType) {  // type → buttonType に変更
        case 'contact':
            // お問い合わせページへの遷移
            window.location.href = '/contact';
            break;
        case 'info':
            // お知らせ一覧ページへの遷移
            window.location.href = '/info';
            break;
        case 'top-page':
            // トップページへの戻る処理
            window.location.href = '/';
            break;
        case 'compare':
            // 条件設定画面への遷移（環境ごとにドメインを切り替え）
            window.open(getCompareUrl(), '_blank');
            break;
        case 'faq':
            // よくあるご質問ページへの遷移
            window.location.href = '/faq';
            break;
        default:
            // デフォルト処理
            break;
    }

    // 親コンポーネントにクリックイベントを通知
    emit('click', event, props.buttonType);  // type → buttonType に変更
};
</script>

<template>
    <button :class="[
        'button-primary',
        `button-primary-${size}`
    ]" type="button" @click="handleClick">
        <!-- メインテキスト -->
        <span class="button-primary_main-text">
            <slot />
        </span>

        <!-- サブテキスト -->
        <span v-if="subText" class="button-primary_sub-text">
            {{ subText }}
        </span>

        <!-- 右矢印アイコン -->
        <Icons name="chevron-right" class="button-primary_arrow" />
    </button>
</template>

<style scoped lang="scss">
// モバイル用のスタイル
.button-primary {
    display: flex;
    position: relative;
    align-items: center;
    justify-content: center;
    border-radius: 999rem;
    font-weight: 700;
    text-align: center;
    line-height: 150%;
    background: linear-gradient(90deg, #1A5D9A 0%, #047878 100%);
    color: #FFF;
    flex-direction: column;

    &:hover {
        background: linear-gradient(90deg, #04B4B4 0%, #4896DC 100%);
    }

    // サイズバリエーション
    &-sm {
        padding: 0.5rem 1rem 0.625rem 1rem;
        font-size: 0.875rem; // 14px
        width: clamp(5.5rem, 90vw, 15rem);
        height: 2.5rem; // 40px

        // サブテキストが存在する場合のみ適用されるスタイル
        &:has(.button-primary_sub-text) {
            width: 13.5rem; // 216px
            height: 3.125rem; // 50px

            .button-primary_main-text {
                font-size: 0.875rem; // 14px
                line-height: 150%;
            }

            .button-primary_sub-text {
                font-size: 0.5rem; // 8px (0.65 × 0.875)
                line-height: 150%;
            }
        }
    }

    &-md {
        padding: 0.625rem 1rem 0.75rem 0.625rem;
        font-size: 1rem;
        width: clamp(15rem, 90vw, 20rem); // 240px to 320px
        height: 3rem; // 48px

        // サブテキストが存在する場合のみ適用されるスタイル
        &:has(.button-primary_sub-text) {
            width: 20rem; // 320px
            height: 3.75rem; // 60px

            .button-primary_main-text {
                font-size: 1rem;
                line-height: 150%;
            }

            .button-primary_sub-text {
                font-size: 0.625rem; // 10px
                line-height: 150%;
            }
        }
    }

    &-lg {
        padding: 0.75rem 1rem 1rem 1rem;
        font-size: 1.25rem;
        width: clamp(15rem, 90vw, 30rem); // 240px to 480px
        height: 3.5rem; // 56px

        // サブテキストが存在する場合のみ適用されるスタイル
        &:has(.button-primary_sub-text) {
            width: 26.5rem; // 424px
            height: 4.375rem; // 70px

            .button-primary_main-text {
                font-size: 1.25rem; // 20px
                line-height: 150%;
            }

            .button-primary_sub-text {
                font-size: 0.75rem; // 12px (0.75 × 1.25)
                line-height: 150%;
            }
        }
    }
}

// 矢印アイコン
.button-primary_arrow {
    position: absolute;
    color: #E5E5E7;
    width: 1.5rem; // 24px
    height: 1.5rem; // 24px
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
}

// デスクトップ用のスタイル
@include mixin.media(pc) {
    .button-primary_arrow {
        right: 1.5rem;
    }
}
</style>
